<template>
	<div class="training-calendar">
		<b-card no-body>
			<b-card-header class="d-flex justify-content-between align-items-center flex-wrap">
				<h4 class="mb-0">Training Sessions Calendar</h4>
				<div class="d-flex align-items-center">
					<b-form-select v-model="year" :options="yearOptions" size="sm" class="mr-1" @change="reloadSessions" />
					<b-button size="sm" variant="outline-primary" @click="goToday">Today</b-button>
				</div>
			</b-card-header>
			<b-card-body>
				<FullCalendar ref="fc" :options="calendarOptions" />
			</b-card-body>
		</b-card>

		<b-modal id="session-details" :title="selectedEvent ? selectedEvent.title : ''" hide-footer v-if="selectedEvent">
			<div>
				<p><strong>Course:</strong> {{ selectedEvent && selectedEvent.extendedProps.event_title }}</p>
				<p><strong>Trainer:</strong> {{ selectedEvent && (selectedEvent.extendedProps.trainer || '—') }}</p>
				<p><strong>Venue:</strong> {{ selectedEvent && (selectedEvent.extendedProps.venue || '—') }}</p>
				<p><strong>Starts:</strong> {{ selectedEvent ? formatDate(selectedEvent.start) : '—' }}</p>
				<p><strong>Ends:</strong> {{ selectedEvent ? formatDate(selectedEvent.end) : '—' }}</p>
				<div v-if="selectedEventDetails" class="mt-1">
					<p><strong>Status:</strong> {{ selectedEventDetails.status || '—' }}</p>
					<p><strong>Max Participants:</strong> {{ selectedEventDetails.max_participants || '—' }}</p>
					<p><strong>Accepted:</strong> {{ selectedEventDetails.accepted_count || 0 }}</p>
					<div v-if="userApplication">
						<b-badge :variant="getMyStatusVariant(selectedEventDetails)">
							{{ getMyStatusText(selectedEventDetails) }}
						</b-badge>
					</div>
				</div>
				<div class="mt-1 d-flex flex-wrap align-items-center">
					<b-button size="sm" variant="primary" class="mr-50 mb-50" @click="goViewEvent(selectedEventDetails)">View Details</b-button>
					<b-button v-if="showApplyButton" size="sm" variant="success" class="mr-50 mb-50" @click="openApplyModal" :disabled="canApplyInfo.loading">
						<span v-if="canApplyInfo.loading"><b-spinner small class="mr-25" />Checking...</span>
						<span v-else>Apply</span>
					</b-button>
				</div>
				<div v-if="!canApplyInfo.loading && selectedEventDetails && !canApplyInfo.can_apply" class="mt-50">
					<p class="text-muted small mb-25">Cannot apply because:</p>
					<ul class="small pl-1 mb-50">
						<li v-if="canApplyInfo.reasons && canApplyInfo.reasons.already_applied">Already applied.</li>
						<li v-if="canApplyInfo.reasons && canApplyInfo.reasons.status_not_open">Event not open.</li>
						<li v-if="canApplyInfo.reasons && canApplyInfo.reasons.deadline_passed">Application deadline passed.</li>
						<li v-if="canApplyInfo.reasons && canApplyInfo.reasons.sessions_ended">All sessions ended.</li>
						<li v-if="canApplyInfo.reasons && canApplyInfo.reasons.not_in_audience">Not in target audience.</li>
					</ul>
				</div>
				<b-button size="sm" variant="primary" @click="$bvModal.hide('session-details')">Close</b-button>
			</div>
		</b-modal>

		<b-modal id="apply-modal" title="Apply to Event" hide-footer v-model="showApplyModal">
			<div v-if="selectedEventDetails">
				<p class="small text-muted" v-if="isEventFull(selectedEventDetails)">Event is full. You may be waitlisted.</p>
				<b-form @submit.prevent="submitApplication">
					<b-form-group label="Reason *" label-for="apply-reason">
						<b-form-textarea id="apply-reason" v-model="applyForm.reason" rows="3" required></b-form-textarea>
					</b-form-group>
					<b-form-group label="Special Needs" label-for="apply-needs">
						<b-form-textarea id="apply-needs" v-model="applyForm.special_needs" rows="2"></b-form-textarea>
					</b-form-group>
					<div class="d-flex justify-content-end">
						<b-button size="sm" variant="outline-secondary" class="mr-50" @click="cancelApply">Cancel</b-button>
						<b-button size="sm" variant="primary" type="submit" :disabled="applyLoading">
							<b-spinner small v-if="applyLoading" class="mr-25" />
							Submit
						</b-button>
					</div>
				</b-form>
			</div>
			<div v-else class="text-muted">Loading...</div>
		</b-modal>
	</div>
</template>

<script>
import FullCalendar from '@fullcalendar/vue'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import listPlugin from '@fullcalendar/list'
import moment from 'moment'
import axiosIns from '@/libs/axios'

export default {
	name: 'TrainingCalendar',
	components: { FullCalendar },
	data() {
		const currentYear = new Date().getFullYear()
		return {
			year: currentYear,
			sessions: [],
			selectedEvent: null,
			selectedEventDetails: null,
			userApplications: [],
			canApplyInfo: { loading: false, can_apply: false, reasons: {}, meta: {} },
			showApplyModal: false,
			applyForm: { reason: '', special_needs: '' },
			applyLoading: false,
			calendarOptions: {
				plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin],
				initialView: 'dayGridMonth',
				headerToolbar: {
					left: 'prev,next today',
					center: 'title',
					right: 'dayGridMonth,timeGridWeek,timeGridDay,listMonth'
				},
				events: [],
				eventClick: this.onEventClick,
				height: 'auto',
				nowIndicator: true,
				selectable: false,
			}
		}
	},
	computed: {
		yearOptions() {
			const start = this.year - 2
			return Array.from({ length: 5 }, (_, i) => {
				const y = start + i
				return { value: y, text: y.toString() }
			})
		},
		userApplication() {
			if (!this.selectedEventDetails) return null
			return this.getUserApplication(this.selectedEventDetails.id)
		}
		,
		showApplyButton() {
			// rely solely on backend decision plus presence of details
			return !!(this.selectedEventDetails && this.canApplyInfo.can_apply)
		}
	},
	mounted() {
		this.reloadSessions()
		this.loadUserApplications()
	},
	methods: {
		reloadSessions() {
			this.fetchSessionsByYear(this.year)
		},
		fetchSessionsByYear(year) {
			// Assumed endpoint pattern based on backend naming conventions
			axiosIns.get(`event-sessions/year/${year}`)
				.then(r => {
					this.sessions = r.data || []
					this.calendarOptions.events = this.mapSessionsToEvents(this.sessions)
				})
				.catch(err => {
					// eslint-disable-next-line no-console
					console.error('Failed to load sessions', err)
				})
		},
		mapSessionsToEvents(sessions) {
			return sessions.map(s => {
				const startIso = s.start_at ? moment(s.start_at, 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM-DDTHH:mm:ss') : null
				const endIso = s.end_at ? moment(s.end_at, 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM-DDTHH:mm:ss') : startIso
				return {
					id: s.id,
					title: s.event_title || 'Session',
					start: startIso,
						end: endIso,
					extendedProps: {
						event_id: s.event_id,
						event_title: s.event_title,
						trainer: s.trainer,
						venue: s.venue,
						sequence_no: s.sequence_no
					}
				}
			})
		},
		onEventClick(info) {
			this.selectedEvent = info.event
			const eventId = info.event.extendedProps.event_id
			Promise.all([
				this.fetchEventDetails(eventId),
				this.fetchCanApply(eventId)
			]).finally(() => {
				this.$bvModal.show('session-details')
			})
		},
		goToday() {
			const ref = this.$refs.fc
			if (ref && typeof ref.getApi === 'function') {
				ref.getApi().today()
			}
		},
		formatDate(dateObj) {
			return dateObj ? moment(dateObj).format('YYYY-MM-DD HH:mm') : '—'
		},
		fetchEventDetails(eventId) {
			if (!eventId) return Promise.resolve()
			return axiosIns.get(`getEventById/${eventId}`).then(r => {
				this.selectedEventDetails = r.data
			}).catch(() => { this.selectedEventDetails = null })
		},
		loadUserApplications() {
			axiosIns.get('dashboard/applications').then(r => {
				this.userApplications = r.data || []
			}).catch(() => { this.userApplications = [] })
		},
		getUserApplication(eventId) {
			return this.userApplications.find(a => a.event_id === eventId)
		},
		getMyStatusVariant(item) {
			const application = this.getUserApplication(item.id)
			if (!application) return 'dark'
			switch (application.status) {
				case 'accepted': return 'success'
				case 'rejected':
				case 'manager_rejected': return 'danger'
				case 'pending_manager_approval': return 'warning'
				case 'pending_admin_review': return 'info'
				case 'waitlisted': return 'secondary'
				case 'withdrawn':
				case 'cancelled':
				case 'draft': return 'dark'
				case 'submitted': return 'primary'
				default: return 'secondary'
			}
		},
		getMyStatusText(item) {
			const application = this.getUserApplication(item.id)
			if (!application) return 'No Application'
			switch (application.status) {
				case 'accepted': return 'Placed'
				case 'rejected':
				case 'manager_rejected': return 'Not Approved'
				case 'pending_manager_approval':
				case 'pending_admin_review':
				case 'submitted': return 'Pending Approval'
				case 'waitlisted': return 'Waitlisted'
				case 'withdrawn': return 'Withdrawn'
				case 'cancelled': return 'Cancelled'
				case 'draft': return 'Draft'
				default: return application.status
			}
		},
		isEventFull(item) {
			if (!item) return false
			const acceptedCount = item.accepted_count || 0
			const maxParticipants = item.max_participants || 0
			return acceptedCount >= maxParticipants && maxParticipants > 0
		},
		isManagerOrAdmin() {
			const userData = JSON.parse(localStorage.getItem('userData')) || {}
			const userRole = userData.role || ''
			return ['admin', 'supermanager', 'Administrator'].includes(userRole)
		},
		fetchCanApply(eventId) {
			if (!eventId) { this.canApplyInfo = { loading:false, can_apply:false, reasons:{}, meta:{} }; return }
			this.canApplyInfo.loading = true
			axiosIns.get(`events/${eventId}/can-apply`)
				.then(r => { this.canApplyInfo = { loading:false, ...r.data } })
				.catch(() => { this.canApplyInfo = { loading:false, can_apply:false, reasons:{ request_failed:true } } })
		},
		openApplyModal() {
			this.showApplyModal = true
		},
		cancelApply() {
			this.showApplyModal = false
			this.applyForm.reason = ''
			this.applyForm.special_needs = ''
		},
		submitApplication() {
			if (!this.selectedEventDetails) return
			if (!this.applyForm.reason.trim()) return
			this.applyLoading = true
			axiosIns.post(`events/${this.selectedEventDetails.id}/applications`, {
				reason: this.applyForm.reason,
				special_needs: this.applyForm.special_needs
			}).then(() => {
				this.cancelApply()
				this.loadUserApplications()
				this.fetchEventDetails(this.selectedEventDetails.id)
			}).finally(() => { this.applyLoading = false })
		},
		goViewEvent(ev) {
			if (!ev || !ev.id) return
			this.$bvModal.hide('session-details')
			this.$router.push({ name: 'dashboard-event-view', params: { id: ev.id } })
		}
	}
}
</script>

<style scoped>
.training-calendar .fc { font-size: 0.85rem; }
.training-calendar .fc-event { cursor: pointer; }
</style>

